import uniqueId from "lodash/uniqueId";
import without from "lodash/without";
import ContactInformationEditField, {
  ContactInformationInput
} from "PFApp/profiles/edit/fields/contact_information_field";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Button } from "PFComponents/button";
import NotFoundComponent from "PFComponents/not_found/not_found";
import { isProfileFieldPermitted } from "PFCore/helpers/profile_permissions";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useCurrentProfile, useCurrentProfileSet } from "PFCore/hooks/queries/profile";
import { editProfile } from "PFCore/services/admin/profiles/edit_profile";
import { editCurrentProfile } from "PFCore/services/current_profile/edit_current_profile";
import { AccessLevel, Profile } from "PFTypes";
import { Ref, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./contact_info_edit_form.module.scss";

type ContactInfoEditFormHandle = {
  onAddNew: () => void;
};

type ContactInfoEditFormProps = {
  profile: Profile;
  adminPage?: boolean;
  onLoading: (isLoading: boolean) => void;
  onChanged: (isChanged: boolean) => void;
  ref?: Ref<ContactInfoEditFormHandle>;
};

const ContactInfoEditForm = ({ profile, adminPage, onLoading, onChanged, ref }: ContactInfoEditFormProps) => {
  const { showGrowl } = useGrowlStoreActions();
  const { data: currentProfile } = useCurrentProfile();
  const setCurrentProfile = useCurrentProfileSet();

  const { t } = useTranslation("profiles", { keyPrefix: "common.contactInfo" });
  const { t: tTranslation } = useTranslation();
  const { getSingleErrorByType } = useResponseErrors();

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasRemoved, setHasRemoved] = useState(false);
  const [contacts, setContacts] = useState<ContactInformationInput[]>(
    () =>
      profile?.contactInformation?.map((item) => ({
        ...item,
        // eslint-disable-next-line camelcase
        input_id: parseInt(uniqueId(), 10)
      })) || []
  );
  const [isEmpty, setIsEmpty] = useState(profile?.contactInformation?.length === 0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsEmpty(contacts?.length === 0);
  }, [contacts]);

  const handleChange = (contact: ContactInformationInput) => {
    const index = contacts.findIndex((cont) => cont.input_id === contact.input_id);
    const newContacts = [...contacts];
    newContacts[index] = contact;
    setContacts(newContacts);
    onChanged && onChanged(true);
  };

  const handleAddNew = () => {
    setIsEmpty(false);
    setContacts((prev) => [
      ...prev,
      {
        // eslint-disable-next-line camelcase
        input_id: parseInt(uniqueId(), 10),
        type: "phone",
        value: ""
      }
    ]);
  };

  const handleUpdate = () => {
    const payload = { contactInformation: contacts };
    setIsLoading(true);
    const promise = adminPage ? editProfile(profile.id, payload) : editCurrentProfile(payload);
    promise
      .then((res) => {
        setErrors({});
        onChanged(false);
        setHasRemoved(false);
        onLoading(false);
        setIsLoading(false);
        showGrowl({
          message: t("infoUpdated"),
          kind: "success"
        });
        !adminPage && setCurrentProfile(res);
      })
      .catch((resp) => {
        const errors = getSingleErrorByType(resp);
        setErrors(errors);
        onLoading(false);
        setIsLoading(false);
      });
  };

  const handleRemove = (contact: ContactInformationInput) => {
    setContacts((prev) => without(prev, contact));
    setErrors({});
    setHasRemoved(true);
    onChanged && onChanged(true);
  };

  useImperativeHandle(ref, () => ({
    onAddNew: handleAddNew
  }));

  const isPermitted = isProfileFieldPermitted(
    currentProfile.permissionsGroup,
    profile?.permissionsGroup?.id,
    "contactInfo",
    AccessLevel.ReadWrite
  );

  return (
    <div ref={ref as unknown as Ref<HTMLDivElement>}>
      {isEmpty && !hasRemoved && (
        <NotFoundComponent
          message={t("noInfoAdded")}
          buttonMessage={t("addNew")}
          buttonDisabled={isLoading || !isPermitted}
          handleClick={handleAddNew}
        />
      )}
      {(!isEmpty || hasRemoved) && (
        <div>
          <div
            style={{ marginTop: 30 }}
            className={css.wrap}
            data-qa-id="profile-edit-contact-info-links-section"
          >
            {contacts?.map((contact, index) => (
              <ContactInformationEditField
                key={contact.input_id}
                contact={contact}
                error={errors[`contact_information[${index}][value]`]}
                locked={!isPermitted}
                lockedTip={t("noPermissionsTip")}
                onChange={handleChange}
                onRemove={handleRemove}
              />
            ))}
            {hasRemoved && (
              <div style={{ display: "block", opacity: 0.7, textAlign: "center", margin: 40 }}>
                {t("someRemoved")}
              </div>
            )}
          </div>
          <div className={css.wrapper}>
            <Button onClick={handleUpdate} disabled={isLoading || !isPermitted}>
              {tTranslation("update")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

ContactInfoEditForm.displayName = "ContactInfoEditForm";

export default ContactInfoEditForm;
